"use client"

import { useState } from "react"
import { Heart, ShoppingBag, ChevronLeft, ChevronRight } from "lucide-react"
// import { Button } from "./ui/button" // Not currently used
import { Link } from "react-router-dom"
import { toast } from "react-hot-toast"

const ProductCard = ({ product = {}, isWishlisted = false, onWishlistToggle, onAddToCart }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  if (!product || Object.keys(product).length === 0) return null

  const images = product.productImages || []

  const nextImage = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setCurrentImageIndex((prev) => (prev + 1) % (images.length || 1))
  }

  const prevImage = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setCurrentImageIndex((prev) => (prev - 1 + (images.length || 1)) % (images.length || 1))
  }

  const getImageSrc = (img) => (typeof img === "string" ? img : img?.url || "")

  const displayImageIndex = currentImageIndex

  const handleWishlistClick = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await onWishlistToggle?.(product._id)
    } catch (err) {
      // Let parent handle errors
    }
  }

  const handleAddToCartClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      onAddToCart?.(product._id)
    } catch {
      toast.error("Failed to add product to cart.")
    }
  }

  return (
    <div className="group bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col h-full">
      {/* Product Image */}
      <Link to={`/product/${product._id}`} state={{ product }} className="block flex-shrink-0">
        <div className="relative aspect-square overflow-hidden bg-gray-50">
          {images.length > 0 ? (
            <img
              src={getImageSrc(images[displayImageIndex]) || "/placeholder.svg"}
              alt={product.productName || "Product Image"}
              className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
            {product.isNew && (
              <span className="bg-[#8B4513] text-white text-xs font-semibold px-2.5 py-1 rounded">
                NEW
              </span>
            )}
            {product.compareAtPriceRs && product.compareAtPriceRs > product.priceRs && (
              <span className="bg-red-500 text-white text-xs font-semibold px-2.5 py-1 rounded">
                {Math.round(((product.compareAtPriceRs - product.priceRs) / product.compareAtPriceRs) * 100)}% OFF
              </span>
            )}
          </div>

          {/* Wishlist Button - Top Right */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleWishlistClick(e);
            }}
            className="absolute top-3 right-3 w-9 h-9 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
          >
            <Heart
              className={`w-5 h-5 transition-colors ${isWishlisted ? "fill-red-500 text-red-500" : "text-gray-600"}`}
            />
          </button>
        </div>

        {/* Image Navigation */}
        {images.length > 1 && (
          <div className="px-4 py-2 bg-gray-50 flex items-center justify-center gap-3 border-t border-gray-100">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                prevImage(e);
              }}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <span className="text-xs text-gray-600 font-medium">
              {currentImageIndex + 1} of {images.length}
            </span>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                nextImage(e);
              }}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        )}

        {/* Product Info */}
        <div className="p-4 bg-white flex-1 flex flex-col min-h-0">
          <h3 className="text-base font-semibold text-gray-900 mb-2 line-clamp-2 h-12">
            {product.productName || "Unnamed Product"}
          </h3>
          
          {/* Description/Features - Fixed height with scroll */}
          {product.description && product.description.length > 0 && (
            <div className="mb-3" style={{ height: '80px', overflow: 'hidden' }}>
              <ul className="text-xs text-gray-600 space-y-1 h-full overflow-y-auto scrollbar-thin pr-1">
                {product.description.map((desc, idx) => (
                  <li key={idx} className="flex items-start py-0.5">
                    <span className="mr-1 flex-shrink-0 mt-0.5">•</span>
                    <span className="flex-1">{desc}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Price Section - Fixed at bottom */}
          <div className="mt-auto">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-xl font-bold text-[#8B4513]">
                £ {product.priceRs?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || "0.00"}
              </span>
              {product.compareAtPriceRs && product.compareAtPriceRs > product.priceRs && (
                <span className="text-sm text-gray-400 line-through">
                  £ {product.compareAtPriceRs.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              )}
            </div>
            {product.brandName && (
              <p className="text-xs text-gray-500">{product.brandName}</p>
            )}
          </div>
        </div>
      </Link>

      {/* Action Buttons - Fixed at bottom */}
      <div className="px-3 sm:px-4 pb-3 sm:pb-4 flex gap-1.5 sm:gap-2 flex-shrink-0">
        <button
          onClick={(e) => {
            e.preventDefault();
            handleWishlistClick(e);
          }}
          className="flex-1 py-2 sm:py-2.5 px-2 sm:px-3 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-1 sm:gap-1.5 whitespace-nowrap overflow-hidden"
          title="Add to Wishlist"
        >
          <Heart className={`w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 ${isWishlisted ? "fill-red-500 text-red-500" : ""}`} />
          <span className="truncate">Wishlist</span>
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            handleAddToCartClick(e);
          }}
          className="flex-1 py-2 sm:py-2.5 px-2 sm:px-3 bg-[#8B4513] text-white rounded-lg text-xs sm:text-sm font-semibold hover:bg-[#654321] transition-colors flex items-center justify-center gap-1 sm:gap-1.5 whitespace-nowrap overflow-hidden"
          title="Buy Now"
        >
          <ShoppingBag className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
          <span className="truncate">Buy now</span>
        </button>
      </div>
    </div>
  )
}

export default ProductCard
